<template>
  <v-dialog
    hide-overlay
    :overlay-opacity="0"
    transition="fade-transition"
    persistent
    ref="dialog"
    v-model="isOpen"
    absolute
  >
    <div class="card-overlay" @click="onOverlayClick"/>

    <v-card style="width: 560px" class="" v-if="isOpen">
      <v-card-text height="800px" class="px-8 pt-8">
        <div class="mb-8 d-flex align-start">
          <div class="d-flex flex-column">
            <span class="font-weight-light p-1 base--text">
              {{ $t('heading.onboarding.import.url.title') }}
            </span>
            <h4 class>{{ $t('heading.onboarding.import.url.subtitle') }}</h4>
          </div>

          <v-btn x-small icon :disabled="loading || preparingImport" @click="closeModal" class="ml-auto">
            <v-icon size="24" color="gray">$close</v-icon>
          </v-btn>
        </div>
        <v-scale-transition>
          <div class="error-box mb-4" v-if="formError && !connectionTestLoading">
            <v-icon color="error" size="20" class="mr-2">$alertwarning</v-icon>
            <span class="error--text">
              <b class="error--text">
                <i18next :translation="$t('message.alert.failed')">
                  <template #message>
                    <span class="font-weight-medium">{{ formError }}</span>
                  </template>
                </i18next>
              </b>
            </span>
          </div>
          <div class="warning-box mb-4" v-if="formWarning && !connectionTestLoading">
            <v-icon color="warning" size="20" class="mr-2">$alertwarning</v-icon>
            <span class="warning--text">
              <b class="warning--text">
                <i18next :translation="$t('message.alert.warning')">
                  <template #message>
                    <span class="font-weight-medium">{{ formWarning }}</span>
                  </template>
                </i18next>
              </b>
            </span>
          </div>
        </v-scale-transition>
        <v-form ref="form" @submit.prevent="handleContinueButtonClick">
          <v-expansion-panels v-model="panelOpen">
            <!-- SIMPLE METHOD: WP admin login details -->
            <v-expansion-panel elevation="0">
              <v-expansion-panel-header
                :class="['h6 text--heading font-weight-bold', { 'disabled-panel': isSimpleMethodDisabled && panelOpen !== 0 }]"
              >
                {{ $t('heading.onboarding.simpleImportMethod.adminLoginDetails.title') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="body-1 gray--text text--darken-2 font-weight-300 mb-4">
                  <div class="mb-2">{{
                      $t('heading.onboarding.simpleImportMethod.adminLoginDetails.description')
                    }}
                  </div>
                  <div>
                    <span class="font-weight-600 me-1">{{
                        $t('heading.onboarding.simpleImportMethod.adminLoginDetails.please_note')
                      }}:</span>
                    <span>{{ $t('heading.onboarding.simpleImportMethod.adminLoginDetails.note') }}</span>
                  </div>
                </div>

                <div v-if="panelOpen === 0">
                  <label
                    class="base--text p-3"
                    v-html="$t('form.label.url')">
                  </label>
                  <v-text-field
                    class="mt-1 mb-3"
                    outlined
                    placeholder="https://example.com/wp-login.php"
                    v-model="url"
                    hide-details=""
                    validate-on-blur
                    :rules="urlRules"
                    @blur="normalizeUrlProtocol"
                    @keydown.enter.prevent="handleContinueButtonClick"
                  />
                  <label
                    class="base--text p-3"
                    v-html="$t('form.label.username')">
                  </label>
                  <v-text-field
                    class="mt-1 mb-3"
                    outlined
                    placeholder="admin"
                    v-model="username"
                    hide-details=""
                    validate-on-blur
                    :rules="usernameRules"
                    @keydown.enter.prevent="handleContinueButtonClick"
                  />
                  <label
                    class="base--text p-3"
                    v-html="$t('form.label.password')">
                  </label>
                  <v-text-field
                    class="mt-1 mb-6"
                    outlined
                    type="password"
                    v-model="password"
                    hide-details=""
                    validate-on-blur
                    :rules="passwordRules"
                    @keydown.enter.prevent="handleContinueButtonClick"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- ADVANCED METHOD: plugin/manual -->
            <v-expansion-panel>
              <v-expansion-panel-header class="h6 text--heading font-weight-bold">
                {{ $t('heading.onboarding.simpleImportMethod.installPluginManually.title') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="panelOpen === 1" class="body-2 gray--text text--darken-2 mb-4">
                  <ol class="p-d">
                    <li class="gray--text text--darken-2">
                      <div>{{
                          $t('heading.onboarding.simpleImportMethod.installPluginManually.downloadPluginHeader')
                        }}
                      </div>
                      <v-btn
                        elevation="0"
                        class="my-3"
                        color="primary"
                        :href="downloadPluginLink"
                      >
                        <v-icon :iconColor="`white`" size="16">$download</v-icon>
                        {{ $t('heading.onboarding.simpleImportMethod.installPluginManually.downloadPluginButton') }}
                      </v-btn>
                    </li>
                    <li class="gray--text text--darken-2 mb-1">
                      {{ $t('heading.onboarding.simpleImportMethod.installPluginManually.installPlugin') }}
                    </li>
                    <li class="gray--text text--darken-2 my-1">
                      {{ $t('heading.onboarding.simpleImportMethod.installPluginManually.runMigration') }}
                    </li>
                  </ol>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-form>
      </v-card-text>

      <v-card-actions
        class="pt-8 d-flex align-center justify-space-between"
        :class="$vuetify.breakpoint.width < 550 ? 'flex-column-reverse' : ''"
      >
        <div :class="$vuetify.breakpoint.width < 550 ? 'mt-4 w-100' : ''">
          <step-indicator :currentStep="maxSteps" :maxSteps="maxSteps"/>
        </div>
        <div
          class="d-flex align-center"
          :class="
            $vuetify.breakpoint.width < 550 ? 'flex-column-reverse w-100' : ''
          "
        >
          <v-btn
            large
            :disabled="loading || preparingImport"
            @click="closeModal"
            text
            :block="$vuetify.breakpoint.width < 550 ? true : false"
            elevation="0"
            class="mr-3"
          >
            <span class="font-weight-bold gray--text text--darken-1">
             {{ $t('button.cancel') }}
            </span>
          </v-btn>
          <v-btn
            large
            @click="handleContinueButtonClick()"
            color="primary"
            :block="$vuetify.breakpoint.width < 550 ? true : false"
            elevation="0"
            :loading="loading || connectionTestLoading || preparingImport"
          >
            <span class="font-weight-bold">
              {{ $t('button.continue') }}
            </span>
            <v-icon class="ml-2 mr-0" size="20">$arrowright</v-icon>

          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import StepIndicator from "../StepIndicator.vue";
import Api from "@/apis/Api";
import FormRulesMixin from "@/mixins/FormRulesMixin";

export default {
  name: "OnboardingImportSimpleMethodModal",
  components: {
    StepIndicator,
  },
  mixins: [FormRulesMixin],
  props: {
    isOpen: Boolean,
    loading: Boolean,
    preparingImport: Boolean,
    maxSteps: Number,
    importInstanceData: Object,
    selectedService: {
      type: Object,
      required: true
    },
  },
  data: () => ({
    panelOpen: 0,
    connectionTestLoading: false,
    url: '',
    username: '',
    password: '',
    formError: '',
    formWarning: '',
    isSimpleMethodDisabled: false,
  }),
  computed: {
    downloadPluginLink() {
      return Api.defaults.baseURL + '/instances/import/download-plugin';
    },
  },
  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },
  methods: {
    handleBeforeUnload(e) {
      if (!this.loading && !this.preparingImport) {
        return;
      }
      const message = 'Import is in progress. Leaving the page may cause the process to fail.';
      (e || window.event).returnValue = message;
      return message;
    },
    closeModal() {
      if (!this.loading && !this.preparingImport) {
        this.$emit('remove-modal');
      }
    },
    onOverlayClick() {
      if (!this.loading && !this.preparingImport && this.$refs.dialog) {
        this.$refs.dialog.animateClick();
      }
    },
    async handleContinueButtonClick() {
      if (this.loading || this.preparingImport || this.connectionTestLoading) return;

      this.formError = "";
      this.formWarning = "";

      try {
        if (
          !this.$refs.form.validate()
          && (this.url === "" || this.username === "" || this.password === "")
        ) {
          throw this.$t('notification.emptyFields');
        }

        this.connectionTestLoading = true;

        let result;
        if (this.isDemo) {
          result = await this.mockConnectionTest();
        } else {
          result = await this.runConnectionTest();
        }

        if (result.error_type === 'error') {
          this.formError = result.message;
          return;
        }

        if (result.error_type === 'connection_error') {
          this.formWarning = result.message;
          this.isSimpleMethodDisabled = true;
          this.panelOpen = 1;

          return;
        }

        if (result.error_type === 'warning') {
          this.formWarning = result.message;
          return;
        }

        if (result.success == true) {
          this.$emit("import-instance", result.import_details);
          return;
        }

        throw this.$t('notification.connectionFailed');
      } catch (error) {
        this.formError = error;
      } finally {
        this.connectionTestLoading = false;
      }
    },
    async runConnectionTest() {
      return new Promise((resolve, reject) => {
        const instanceUrl = (this.importInstanceData?.url || "").trim();
        let params = {
          type: 'plugin',
          service_id: this.selectedService.id,
          url: instanceUrl,
          login_url: (this.url || "").trim(),
          username: this.username,
          password: this.password,
          using_admin_login_credentials: this.panelOpen === 0,
        };

        Api.post(`/instances/import/test-wordpress-connection`, params)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(Api.getErrorMessage(error));
          });
      });
    },
    async mockConnectionTest() {
      return new Promise((resolve, reject) => {
        if (this.hostname !== "reject") {
          setTimeout(() => {
            resolve({
              success: true,
            });
          }, 2000);
        } else {
          reject(false);
        }
      });
    },
    resetData() {
      this.panelOpen = 0;

      this.url = "";
      this.username = "";
      this.password = "";

      this.formError = "";
      this.formWarning = "";

      this.connectionTestLoading = false;
      this.isSimpleMethodDisabled = false;
    },
    setInitialUrlFromImportInstanceData() {
      const rawUrl = (this.importInstanceData?.url || "").trim();

      if (!rawUrl) {
        this.url = "";
        return;
      }

      let finalUrl = rawUrl;

      try {
        const parsed = new URL(rawUrl);

        if (parsed.pathname === "/" || parsed.pathname === "") {
          const trimmed = rawUrl.replace(/\/$/, "");
          finalUrl = `${trimmed}/wp-login.php`;
        }
      } catch (e) {
        const hostOnlyPattern = /^https?:\/\/[^/]+\/?$/i;

        if (hostOnlyPattern.test(rawUrl)) {
          const trimmed = rawUrl.replace(/\/$/, "");
          finalUrl = `${trimmed}/wp-login.php`;
        }
      }

      this.url = finalUrl;
    },
    normalizeUrlProtocol() {
      const value = (this.url || "").trim();
      if (value === "") {
        this.url = "https://";
        return;
      }
      const hasProtocol = /^https?:\/\//i.test(value);
      if (!hasProtocol) {
        this.url = `https://${value}`;
      }
    },
  },
  watch: {
    isOpen: function (value) {
      this.resetData();

      if (value) {
        this.setInitialUrlFromImportInstanceData();
        this.$nextTick(() => {
          this.$store.dispatch("lockBodyScroll");
        });
      } else {
        this.$store.dispatch("unlockBodyScroll");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.v-dialog__content::v-deep {
  .v-dialog {
    overflow-y: auto;
    overflow-x: hidden;
  }
}

.v-card__text {
  -webkit-line-clamp: unset !important;
}

.v-expansion-panel {
  border: 1px solid var(--v-gray-lighten2);

  &::before {
    display: none;
  }

  &::after {
    display: none;
  }

  &--active {
    border-color: var(--v-primary-base);
  }

  .v-expansion-panel-content::v-deep {
    .v-expansion-panel-content__wrap {
      padding-bottom: 0px;
      color: var(--v-gray-lighten1);
    }
  }

  .v-expansion-panel-header {
    padding-left: 60px;
    position: relative;

    &::before,
    &::after {
      content: "";
      position: absolute;
      left: 24px;
      top: 50%;
      transform: translateY(-50%);
      opacity: 1 !important;
      background: var(--v-checkbox-base);
      border-radius: 20px;
    }

    &::before {
      transition: border-color 0.24s ease, background-color 0.24s ease;
      width: 20px;
      height: 20px;
      border: 1px solid var(--v-gray-lighten2);
    }

    &::after {
      width: 10px;
      height: 10px;
      z-index: 10;
      left: 29px;
    }

    &:hover {
      &::before {
        border-color: var(--v-primary-base);
      }
    }

    &--active {
      &:before {
        border-color: var(--v-primary-base);
        background-color: var(--v-primary-base);
      }
    }
  }
}

.disabled-panel {
  &,
  & * {
    color: var(--v-gray-lighten2) !important;
  }

  &::before {
    border-color: var(--v-gray-lighten3);
    background-color: var(--v-gray-lighten4);
  }

  &::after {
    background-color: var(--v-gray-lighten3);
  }
}

.error-box {
  background-color: var(--v-error-lighten5);
  border-radius: 8px;
  padding: 16px;
  display: flex;
}

.warning-box {
  background-color: var(--v-warning-lighten5);
  border-radius: 8px;
  padding: 16px;
  display: flex;
}

@media (max-width: 480px) {
  .v-card__text,
  .v-card__actions {
    padding-left: 24px !important;
    padding-right: 24px !important;
  }
}
</style>
